import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { TrendingUp, AlertCircle, CheckCircle, ArrowRight, ArrowLeft, Sparkles, Target, Zap, Crown, Info, Calculator, Gift, CalendarDays, Flame } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRESET_META = {
  500: { tag: 'Starter', icon: Target, color: 'from-sky-400 to-blue-500', est: '30-40' },
  1000: { tag: 'Recommended', icon: TrendingUp, color: 'from-blue-500 to-indigo-600', est: '70-90', popular: true },
  2000: { tag: 'Scale', icon: Crown, color: 'from-amber-500 to-orange-600', est: '150-180' },
};

const PACK_ICON = { flex: Zap, week: CalendarDays, month: Crown };

const inr = (n) => '\u20b9' + Number(n || 0).toLocaleString('en-IN');

/**
 * AdsBudgetSelection - Step 4 of Client Onboarding
 * - Daily budget PER PRODUCT via capsules (₹500/1000/2000) + Manual. Min ₹500.
 * - Duration Packs: Flexible (min 3 days), 7-Day (1 day FREE), 1-Month (5 days FREE + Ads Credit per plan).
 */
const AdsBudgetSelection = ({
  selectedProducts = [],
  selectedBudget,
  planName = 'Basic',
  onBudgetChange,
  onNext,
  onBack,
  hideNextButton = false,
}) => {
  const numProducts = Math.max(1, (selectedProducts || []).length || 1);

  const [config, setConfig] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await axios.get(`${API}/ads-config`); if (data && data.daily) setConfig(data); } catch (_e) { /* ignore */ }
    })();
  }, []);

  const daily = config?.daily || { min: 500, step: 100, presets: [500, 1000, 2000], minDays: 3, defaultPerProduct: 1000 };
  const packs = config?.packs || [];
  const minPerProduct = daily.min || 500;
  const step = daily.step || 100;
  const minDays = daily.minDays || 3;

  const [perProduct, setPerProduct] = useState(selectedBudget?.perProduct ?? 1000);
  const [isCustom, setIsCustom] = useState(false);
  const [customInput, setCustomInput] = useState('');
  const [packId, setPackId] = useState(selectedBudget?.packId || 'week');
  const [flexDays, setFlexDays] = useState(selectedBudget?.days && selectedBudget?.packId === 'flex' ? selectedBudget.days : Math.max(minDays, 5));
  const [error, setError] = useState('');

  // Initialise custom state once config loads
  useEffect(() => {
    if (!config) return;
    const presets = daily.presets || [500, 1000, 2000];
    if (!presets.includes(perProduct)) {
      setIsCustom(true);
      setCustomInput(String(perProduct));
    }
  }, [config]);

  const activePack = useMemo(() => packs.find((p) => p.id === packId) || packs[0] || { id: 'flex', days: 0, freeDays: 0, adsCreditByPlan: {} }, [packs, packId]);

  const totalDays = activePack.id === 'flex' ? flexDays : (activePack.days || flexDays);
  const freeDays = activePack.freeDays || 0;
  const paidDays = Math.max(1, totalDays - freeDays);
  const adsCredit = (activePack.adsCreditByPlan && activePack.adsCreditByPlan[planName]) || 0;

  const totalDaily = useMemo(() => perProduct * numProducts, [perProduct, numProducts]);
  const payable = useMemo(() => totalDaily * paidDays, [totalDaily, paidDays]);
  const fullValue = useMemo(() => totalDaily * totalDays, [totalDaily, totalDays]);
  const savings = fullValue - payable;

  const validate = (value) => {
    if (!value || isNaN(value)) return 'Enter a valid amount';
    const v = parseInt(value, 10);
    if (v < minPerProduct) return `Minimum ${inr(minPerProduct)} per product`;
    if (v % step !== 0) return `Amount must be a multiple of ${inr(step)}`;
    return '';
  };

  const handlePresetSelect = (value) => {
    setIsCustom(false);
    setPerProduct(value);
    setCustomInput('');
    setError('');
  };

  const handleCustomChange = (val) => {
    const cleaned = val.replace(/[^0-9]/g, '');
    setCustomInput(cleaned);
    setIsCustom(true);
    if (cleaned === '') { setError(''); return; }
    const num = parseInt(cleaned, 10);
    setPerProduct(num);
    setError(validate(num));
  };

  const handleCustomBlur = () => {
    if (isCustom && customInput) {
      const num = parseInt(customInput, 10);
      const rounded = Math.max(minPerProduct, Math.round(num / step) * step);
      setCustomInput(String(rounded));
      setPerProduct(rounded);
      setError(validate(rounded));
    }
  };

  // Auto-save budget whenever selections change (so parent always has latest, needed when hideNextButton=true)
  useEffect(() => {
    if (!config) return; // wait for config to load
    const err = validate(perProduct);
    if (err) return; // don't propagate invalid values
    onBudgetChange?.({
      perProduct,
      total: totalDaily,
      days: totalDays,
      paidDays,
      freeDays,
      numProducts,
      totalCampaign: payable,
      adsCredit,
      packId: activePack.id,
      planName,
      isCustom,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perProduct, packId, flexDays, config]);

  const handleContinue = () => {
    const err = validate(perProduct);
    if (err) { setError(err); return; }
    onBudgetChange?.({
      perProduct,
      total: totalDaily,
      days: totalDays,
      paidDays,
      freeDays,
      numProducts,
      totalCampaign: payable,
      adsCredit,
      packId: activePack.id,
      planName,
      isCustom,
    });
    onNext?.();
  };

  const presets = daily.presets || [500, 1000, 2000];

  return (
    <div className="max-w-3xl mx-auto px-2 sm:px-0">
      {/* Header */}
      <div className="text-center mb-5 sm:mb-7">
        <div className="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 bg-blue-100 border border-blue-200 rounded-full mb-3 sm:mb-4">
          <Calculator className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2 text-blue-600" />
          <span className="text-blue-700 text-xs sm:text-sm font-semibold">
            Min {inr(minPerProduct * numProducts)}/day for {numProducts} {numProducts === 1 ? 'product' : 'products'}
          </span>
        </div>
        <h2 className="text-xl sm:text-3xl md:text-4xl font-extrabold text-slate-800 mb-2">Select Your Ads Budget</h2>
        <p className="text-slate-500 text-xs sm:text-base px-2">Daily budget per product — min {inr(minPerProduct)}, multiples of {inr(step)}</p>
      </div>

      {/* Preset capsules */}
      <div className="grid grid-cols-3 gap-2.5 sm:gap-4 mb-3">
        {presets.map((value) => {
          const meta = PRESET_META[value] || { tag: 'Budget', icon: Target, color: 'from-blue-500 to-indigo-600', est: '' };
          const Icon = meta.icon;
          const selected = !isCustom && perProduct === value;
          return (
            <Card key={value} onClick={() => handlePresetSelect(value)}
              className={`relative cursor-pointer transition-all duration-300 active:scale-95 overflow-hidden ${selected ? 'border-2 border-blue-500 shadow-lg shadow-blue-500/30 scale-[1.02] bg-blue-50' : 'border-2 border-slate-200 hover:border-blue-300 hover:shadow-md bg-white'}`}>
              {meta.popular && (<div className="absolute top-0 right-0 bg-orange-500 text-white text-[9px] sm:text-[10px] font-extrabold px-2 py-0.5 rounded-bl-lg">POPULAR</div>)}
              {selected && (<div className="absolute top-2 left-2 w-5 h-5 sm:w-6 sm:h-6 bg-blue-500 rounded-full flex items-center justify-center shadow"><CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-white" /></div>)}
              <CardContent className="p-3 sm:p-5 text-center">
                <div className={`w-9 h-9 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3 rounded-lg sm:rounded-xl bg-gradient-to-br ${meta.color} flex items-center justify-center shadow`}>
                  <Icon className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
                </div>
                <div className="mb-1">
                  <span className="text-lg sm:text-2xl font-extrabold text-slate-800 leading-none">{inr(value)}</span>
                  <span className="text-slate-500 text-[10px] sm:text-xs">/day</span>
                </div>
                <p className="text-slate-500 text-[10px] sm:text-xs mb-1">{meta.tag}</p>
                {meta.est && (<div className="bg-green-50 border border-green-200 rounded-md px-1.5 py-1"><p className="text-[8px] sm:text-[10px] text-slate-500 leading-none">Est. Orders/mo</p><p className="text-green-700 font-bold text-[11px] sm:text-sm leading-tight">{meta.est}</p></div>)}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Manual custom */}
      <Card className={`mb-5 transition-all ${isCustom ? 'border-2 border-blue-500 bg-blue-50 shadow-lg shadow-blue-500/20' : 'border-2 border-slate-200 bg-white'}`}>
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 flex-1">
              <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isCustom ? 'bg-blue-500' : 'bg-slate-200'}`}>
                <Sparkles className={`w-4 h-4 sm:w-5 sm:h-5 ${isCustom ? 'text-white' : 'text-slate-500'}`} />
              </div>
              <div className="min-w-0">
                <p className="text-sm sm:text-base font-bold text-slate-800 leading-tight">Manual Budget</p>
                <p className="text-[10px] sm:text-xs text-slate-500">Multiples of {inr(step)} • Min {inr(minPerProduct)}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:flex-shrink-0">
              <span className="text-lg sm:text-xl font-bold text-slate-700">₹</span>
              <Input type="text" inputMode="numeric" placeholder="2500" value={customInput}
                onChange={(e) => handleCustomChange(e.target.value)} onBlur={handleCustomBlur} onFocus={() => setIsCustom(true)}
                className="w-full sm:w-32 h-11 bg-white border-slate-300 font-bold text-base text-center" data-testid="ads-manual-input" />
              <span className="text-xs text-slate-500 hidden sm:inline">/day</span>
            </div>
          </div>
          {isCustom && error && (<div className="mt-2 flex items-center gap-1.5 text-xs text-red-600"><AlertCircle className="w-3.5 h-3.5 flex-shrink-0" /><span>{error}</span></div>)}
          {isCustom && !error && customInput && (<div className="mt-2 flex items-center gap-1.5 text-xs text-green-600"><CheckCircle className="w-3.5 h-3.5 flex-shrink-0" /><span>Valid amount</span></div>)}
        </CardContent>
      </Card>

      {/* Duration Packs */}
      <div className="mb-2 flex items-center gap-2">
        <CalendarDays className="w-4 h-4 text-blue-600" />
        <p className="text-sm sm:text-base font-bold text-slate-800">Choose Campaign Pack</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
        {packs.map((pk) => {
          const Icon = PACK_ICON[pk.id] || Zap;
          const selected = packId === pk.id;
          const credit = (pk.adsCreditByPlan && pk.adsCreditByPlan[planName]) || 0;
          return (
            <button key={pk.id} type="button" onClick={() => setPackId(pk.id)} data-testid={`ads-pack-${pk.id}`}
              className={`relative text-left rounded-2xl border-2 p-4 transition-all active:scale-95 ${selected ? 'border-blue-600 bg-blue-50 shadow-lg shadow-blue-500/20' : 'border-slate-200 bg-white hover:border-blue-300'}`}>
              {pk.badge && (<span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-orange-500 to-red-500 text-white text-[10px] font-extrabold px-2.5 py-0.5 rounded-full whitespace-nowrap shadow">{pk.badge}</span>)}
              <div className="flex items-center gap-2 mb-1.5">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${selected ? 'bg-blue-600' : 'bg-slate-100'}`}>
                  <Icon className={`w-4 h-4 ${selected ? 'text-white' : 'text-slate-500'}`} />
                </div>
                <span className="font-bold text-slate-800 text-sm">{pk.label}</span>
                {selected && <CheckCircle className="w-4 h-4 text-blue-600 ml-auto" />}
              </div>
              <p className="text-[11px] text-slate-500 leading-snug min-h-[32px]">{pk.tagline}</p>
              {pk.freeDays > 0 && (<div className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold text-green-600"><Gift className="w-3.5 h-3.5" />{pk.freeDays} day{pk.freeDays > 1 ? 's' : ''} free</div>)}
              {credit > 0 && (<div className="mt-1 inline-flex items-center gap-1 text-[11px] font-bold text-amber-600"><Sparkles className="w-3.5 h-3.5" />+{inr(credit)} ads credit</div>)}
            </button>
          );
        })}
      </div>

      {/* Flexible days selector */}
      {activePack.id === 'flex' && (
        <Card className="mb-4 border-slate-200">
          <CardContent className="p-3 sm:p-4">
            <p className="text-xs sm:text-sm font-semibold text-slate-700 mb-2">Campaign Duration (min {minDays} days)</p>
            <div className="flex flex-wrap gap-2">
              {[3, 5, 7, 10, 15].filter((d) => d >= minDays).map((d) => (
                <button key={d} type="button" onClick={() => setFlexDays(d)}
                  className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-semibold border-2 transition-all active:scale-95 ${flexDays === d ? 'bg-blue-600 text-white border-blue-600 shadow' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'}`}>
                  {d} days
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary */}
      <div className="p-3 sm:p-5 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl mb-4">
        <div className="flex items-center gap-2 mb-3">
          <Calculator className="w-4 h-4 sm:w-5 sm:h-5 text-green-700" />
          <h3 className="text-sm sm:text-base font-bold text-slate-800">Budget Summary</h3>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center mb-3">
          <div className="bg-white/70 rounded-lg p-2 sm:p-3">
            <p className="text-[10px] sm:text-xs text-slate-500">Per Product</p>
            <p className="text-base sm:text-xl font-extrabold text-slate-800">{inr(perProduct)}</p>
            <p className="text-[9px] sm:text-[10px] text-slate-500">/ day</p>
          </div>
          <div className="bg-white/70 rounded-lg p-2 sm:p-3 ring-2 ring-blue-200">
            <p className="text-[10px] sm:text-xs text-slate-500">Daily Total</p>
            <p className="text-base sm:text-xl font-extrabold text-blue-700">{inr(totalDaily)}</p>
            <p className="text-[9px] sm:text-[10px] text-slate-500">{numProducts}p × {inr(perProduct)}</p>
          </div>
          <div className="bg-white/70 rounded-lg p-2 sm:p-3">
            <p className="text-[10px] sm:text-xs text-slate-500">{totalDays}-Day Pack</p>
            <p className="text-base sm:text-xl font-extrabold text-green-700">{inr(payable)}</p>
            <p className="text-[9px] sm:text-[10px] text-slate-500">{paidDays} paid{freeDays > 0 ? ` + ${freeDays} free` : ''}</p>
          </div>
        </div>
        {(savings > 0 || adsCredit > 0) && (
          <div className="flex flex-wrap items-center justify-center gap-2 text-xs sm:text-sm">
            {savings > 0 && (
              <span className="inline-flex items-center gap-1 bg-green-600 text-white font-bold px-2.5 py-1 rounded-full">
                <Gift className="w-3.5 h-3.5" /> You save {inr(savings)} <span className="line-through opacity-70 font-medium">{inr(fullValue)}</span>
              </span>
            )}
            {adsCredit > 0 && (
              <span className="inline-flex items-center gap-1 bg-amber-500 text-white font-bold px-2.5 py-1 rounded-full">
                <Sparkles className="w-3.5 h-3.5" /> +{inr(adsCredit)} extra ads credit
              </span>
            )}
          </div>
        )}
      </div>

      {/* Urgency */}
      <div className="flex items-center justify-center gap-2 mb-4 text-xs sm:text-sm text-red-600 font-semibold">
        <Flame className="w-4 h-4 text-orange-500 animate-pulse" />
        Packs &amp; free-day offers are limited — lock yours now before it&apos;s gone!
      </div>

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
        <div className="flex items-start gap-2 sm:gap-3">
          <Info className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-[11px] sm:text-sm text-slate-700">
            <p className="font-semibold text-slate-800 mb-1">How it works:</p>
            <ul className="list-disc list-inside space-y-0.5 text-slate-600">
              <li>Pay ads budget only AFTER your store goes live</li>
              <li>Used for Facebook &amp; Instagram advertising</li>
              <li>Longer packs give free days &amp; bonus ads credit</li>
              <li>Adjustable anytime from your dashboard</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col-reverse sm:flex-row items-center justify-between gap-3">
        <Button variant="outline" onClick={onBack} className="w-full sm:w-auto"><ArrowLeft className="w-4 h-4 mr-1.5" /> Back</Button>
        {!hideNextButton && (
          <Button onClick={handleContinue} disabled={!!error} data-testid="ads-budget-next-btn"
            className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 sm:px-8 py-3 sm:py-4 font-bold shadow-lg shadow-blue-500/30 active:scale-95">
            Next <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default AdsBudgetSelection;
